import { useEffect } from 'react';
import globalWindow from '@shared/core/globals';
import { useCardCustomizerTelemetry } from '../routes/CardCustomizer/CardCustomizer.telemetry';
import { clampToSupportedWeight, FONT_FAMILIES, SELF_HOSTED, SELF_HOSTED_SYSTEM } from '../constants/fonts';

const FONT_HOST = 'https://withjoy.com/assets/public/messaging/fonts/';

function loadFont(family: string, onError: (family: string, error: unknown) => void) {
  const [name, modifier] = family.split(':'); // `family` may be of format "Literata:400italic"
  const woff = SELF_HOSTED[name] ?? SELF_HOSTED_SYSTEM[name];
  if (woff) {
    void (async () => {
      try {
        const font = new FontFace(name, `url('${FONT_HOST}${woff}')`);
        await font.load();
        // document.fonts() is not supported in Edge 17 (released April 2018)
        // eslint-disable-next-line compat/compat
        document.fonts.add(font);
      } catch (error) {
        onError(name, error);
      }
    })();
    return;
  }

  // For Google-hosted fonts, clamp the weight to a supported value so we
  // never request e.g. "Parisienne:700" (which returns HTTP 400).
  let googleFamily = family;
  if (modifier) {
    const weightMatch = modifier.match(/^(\d+)/);
    if (weightMatch) {
      const requestedWeight = parseInt(weightMatch[1], 10);
      const clampedWeight = clampToSupportedWeight(name, requestedWeight);
      const suffix = modifier.slice(weightMatch[1].length); // e.g. "italic"
      googleFamily = `${name}:${clampedWeight}${suffix}`;
    }
  }

  // Look in the Network Tab for URLs of this pattern to inspect:
  // https://fonts.googleapis.com/css?family=Literata:400italic
  globalWindow?.WebFont?.load({
    google: {
      families: [googleFamily],
    },
  });
}

export function useFont(family: string) {
  const { loadFontError } = useCardCustomizerTelemetry();
  useEffect(() => loadFont(family, loadFontError), [family, loadFontError]);
}

// Not a permanent solution, using this for MVP internal launch
// For TextLayerToolbar font picker preview, not for individual layers.
export function useAllFonts() {
  const { loadFontError } = useCardCustomizerTelemetry();
  useEffect(() => {
    for (const family of FONT_FAMILIES) {
      loadFont(family, loadFontError);
    }
  }, [loadFontError]);
}
